// `/admin/sends` — the Review tab: the auto lane's drafted sequences, waiting for a human (W9).
//
// Admin and leadership (`can_approve`); every route behind it is `require_book_approve`. Each
// approval re-runs the door gate, the 30-day rule, the exclusion list and the daily cap on the
// server, and while `SCOUT_AUTO_SEND` is off (or on a preview) it is a dry run that still
// records the decision. The rates table at the bottom is the readiness metric.

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccessDeniedError, approveAutoSend, approveAutoSendBulk, fetchAutoSendQueue, fetchMe, panelMessage,
  rejectAutoSend, resolveAutoSend,
} from '../api/client';
import type { AutoSendQueuePayload, ContactEditBody, SendResult, SendRow } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { AdminSection } from '../admin/AdminSection';
import { PageChrome } from '../components/PageChrome';
import { SendRowCard } from '../sends/SendRowCard';
import { approveAllQuestion, capsLine, contestLine, modeLine, rateLine, resultLine, sendersLine } from '../lib/sends';
import { formatCount } from '../lib/format';

const QUEUE_KEY = ['auto-send-queue'];

function ResultsList({ results, names }: { results: SendResult[]; names: Map<string, string> }) {
  if (!results.length) return null;
  return (
    <ul className="sendresults" role="status">
      {results.map(result => {
        const line = resultLine(result.outcome, result.detail);
        return (
          <li key={result.account_id + result.outcome}><b>{line.word}</b>{' · '}
            {names.get(result.account_id) ?? result.account_id}{line.detail ? ` — ${line.detail}` : ''}</li>
        );
      })}
    </ul>
  );
}

// Account names by id across every list, so an outcome line names the company.
function accountNames(queue: AutoSendQueuePayload | undefined): Map<string, string> {
  const names = new Map<string, string>();
  for (const row of [...(queue?.pending ?? []), ...(queue?.attention ?? []), ...(queue?.recent ?? [])]) {
    names.set(row.account_id, row.account);
  }
  return names;
}

function RowList({
  rows,
  empty,
  rejectReasons,
  selectable,
  selected,
  busy,
  onToggle,
  onApprove,
  onReject,
  onResolve,
}: {
  rows: SendRow[];
  empty: string;
  rejectReasons: string[];
  selectable: boolean;
  selected: Set<string>;
  busy: boolean;
  onToggle: (accountId: string) => void;
  onApprove: (accountId: string, edits: ContactEditBody[]) => void;
  onReject: (accountId: string, reason: string, note: string) => void;
  onResolve: (accountId: string, outcome: 'sent' | 'not_sent') => void;
}) {
  if (!rows.length) return <p className="adminnote">{empty}</p>;
  return (
    <div className="sendlist">
      {rows.map(row => (
        <SendRowCard key={row.account_id} row={row} rejectReasons={rejectReasons} selectable={selectable}
          selected={selected.has(row.account_id)} busy={busy} onToggle={onToggle} onApprove={onApprove}
          onReject={onReject} onResolve={onResolve} />
      ))}
    </div>
  );
}

export function SendsView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<SendResult[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const viewer = meQuery.data?.viewer;
  const allowed = Boolean(viewer?.can_approve);
  const queueQuery = useQuery({ queryKey: QUEUE_KEY, queryFn: () => fetchAutoSendQueue(getToken), enabled: allowed });

  // Every write answers with the refreshed queue, so the page redraws from that one reply.
  function settle(payload: AutoSendQueuePayload) {
    queryClient.setQueryData(QUEUE_KEY, payload);
    queryClient.invalidateQueries({ queryKey: ['auto-send-count'] });
    setResults(payload.results ?? []);
    setActionError(null);
    setSelected(new Set());
  }
  function fail(error: unknown) {
    setActionError(panelMessage(error, 'Could not save that decision'));
  }
  const approve = useMutation({
    mutationFn: (input: { accountId: string; edits: ContactEditBody[] }) =>
      approveAutoSend(getToken, input.accountId, input.edits),
    onSuccess: settle, onError: fail,
  });
  const bulk = useMutation({
    mutationFn: (accountIds: string[]) => approveAutoSendBulk(getToken, accountIds),
    onSuccess: settle, onError: fail,
  });
  const reject = useMutation({
    mutationFn: (input: { accountId: string; reason: string; note: string }) =>
      rejectAutoSend(getToken, input.accountId, input.reason, input.note),
    onSuccess: settle, onError: fail,
  });
  const resolve = useMutation({
    mutationFn: (input: { accountId: string; outcome: 'sent' | 'not_sent' }) =>
      resolveAutoSend(getToken, input.accountId, input.outcome),
    onSuccess: settle, onError: fail,
  });
  const busy = approve.isPending || bulk.isPending || reject.isPending || resolve.isPending;

  if (meQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={meQuery.error.message} email={email} onSignOut={signOut} />;
  }
  if (meQuery.isPending) return <div className="loading">Signing you in…</div>;
  // Hide, do not stub: someone without the approve grant lands on their own board.
  if (viewer && !viewer.can_approve) {
    return <Navigate to={viewer.default_view === 'execution' ? '/execution' : '/activity'} replace />;
  }

  const queue = queueQuery.data;
  const settings = queue?.settings ?? null;
  const pending = queue?.pending ?? [];
  const rejectReasons = settings?.reject_reasons ?? [];

  function toggle(accountId: string) {
    setSelected(current => {
      const next = new Set(current);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }
  function onApprove(accountId: string, edits: ContactEditBody[]) {
    approve.mutate({ accountId, edits });
  }
  function onResolve(accountId: string, outcome: 'sent' | 'not_sent') {
    resolve.mutate({ accountId, outcome });
  }
  function onReject(accountId: string, reason: string, note: string) {
    reject.mutate({ accountId, reason, note });
  }

  // Live, "Approve all" pushes every queued account in one click, so it asks first. A dry run does not.
  function approveAll() {
    const leadCount = pending.reduce((total, row) => total + row.contacts.length, 0);
    const question = approveAllQuestion(settings, pending.length, leadCount);
    if (question && !window.confirm(question)) return;
    bulk.mutate(pending.map(row => row.account_id));
  }

  return (
    <PageChrome freshness={null} viewer={viewer}>
      <p className="sub">
        Scout's drafted sequences for the bank's auto lane, each sent from its sender's sequence once approved
        ({(settings?.senders ?? []).length > 1 ? 'the sender is shown on every card' : (settings?.sender_email || 'the sender')}). Tier-A accounts go to a strategist through the approval queue instead.
      </p>
      {queueQuery.error ? <div className="err"><b>Could not load the queue.</b><br />{(queueQuery.error as Error).message}</div> : null}
      {settings ? <p className={'sendmode ' + (settings.live ? 'live' : 'dry')}>{modeLine(settings)}<br />{capsLine(settings)}
        {sendersLine(settings) ? <><br />{sendersLine(settings)}</> : null}
        {contestLine(settings) ? <><br />{contestLine(settings)}</> : null}</p> : null}
      {queue?.counts?.stale ? (
        <p className="adminnote">{formatCount(queue.counts.stale)} accounts drafted before the writers' contest are
          waiting for tonight's redraft.</p>
      ) : null}
      {actionError ? <div className="err">{actionError}</div> : null}
      <ResultsList results={results} names={accountNames(queue)} />

      <AdminSection
        title="Waiting for review"
        count={queue ? `${formatCount(pending.length)} accounts` : null}
        loading={queueQuery.isPending}
        unavailable={queue?.unavailable}
      >
        <div className="sendbulk">
          <button className="btn primary" type="button" disabled={busy || selected.size === 0}
            onClick={() => bulk.mutate([...selected])}>
            Approve selected ({selected.size})
          </button>
          <button className="btn" type="button" disabled={busy || pending.length === 0}
            onClick={approveAll}>
            Approve all {pending.length} as written
          </button>
        </div>
        <RowList rows={pending} empty="Nothing waiting. The auto lane drafts overnight; new sequences land here by morning."
          rejectReasons={rejectReasons} selectable selected={selected} busy={busy}
          onToggle={toggle} onApprove={onApprove} onReject={onReject} onResolve={onResolve} />
      </AdminSection>

      <AdminSection title="Needs a look" count={queue ? `${formatCount(queue.attention.length)} held, dry-run, failed or unconfirmed` : null}
        loading={queueQuery.isPending} unavailable={queue?.unavailable}>
        <RowList rows={queue?.attention ?? []} empty="Nothing held, failed or unconfirmed." rejectReasons={rejectReasons} selectable={false} selected={selected}
          busy={busy} onToggle={toggle} onApprove={onApprove} onReject={onReject} onResolve={onResolve} />
      </AdminSection>

      <AdminSection title="How reviews are going" loading={queueQuery.isPending} unavailable={queue?.unavailable}>
        <p className="adminnote">Ready to run on its own when two weeks show at least 90% approved as written
          and no "never contact" rejections.</p>
        <table className="sendrates">
          <tbody>
            {(queue?.rates ?? []).map(rate => (
              <tr key={rate.day}><td>{rate.day}</td><td>{rateLine(rate)}</td></tr>
            ))}
          </tbody>
        </table>
      </AdminSection>

      <AdminSection title="Tier A, asked for a strategist" count={queue ? `${formatCount(queue.routed.length)} recent` : null}
        loading={queueQuery.isPending} unavailable={queue?.unavailable}>
        {queue?.routed.length ? (
          <ul className="sendrouted">
            {queue.routed.map(route => (
              <li key={route.account_id}>{route.routed_on} · <b>{route.account}</b> → {route.strategist_email}
                {' '}({route.rules.join('; ')}) · {route.outcome}{route.detail ? ` — ${route.detail}` : ''}</li>
            ))}
          </ul>
        ) : <p className="adminnote">No tier-A routing yet. Approve or decline them on Available.</p>}
      </AdminSection>

      <AdminSection title="Recently decided" count={queue ? `${formatCount(queue.recent.length)} shown` : null}
        loading={queueQuery.isPending} unavailable={queue?.unavailable}>
        <RowList rows={queue?.recent ?? []} empty="No decisions yet." rejectReasons={rejectReasons} selectable={false} selected={selected}
          busy={busy} onToggle={toggle} onApprove={onApprove} onReject={onReject} onResolve={onResolve} />
      </AdminSection>
    </PageChrome>
  );
}
